import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetFixSessionMessagesQueryResponse, GetFixSessionMessagesPathParams, GetFixSessionMessagesQueryParams, GetFixSessionMessages404 } from "../types/GetFixSessionMessages";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetFixSessionMessagesClient = typeof client<GetFixSessionMessagesQueryResponse, GetFixSessionMessages404, never>;
type GetFixSessionMessages = {
    data: GetFixSessionMessagesQueryResponse;
    error: GetFixSessionMessages404;
    request: never;
    pathParams: GetFixSessionMessagesPathParams;
    queryParams: GetFixSessionMessagesQueryParams;
    headerParams: never;
    response: GetFixSessionMessagesQueryResponse;
    client: {
        parameters: Partial<Parameters<GetFixSessionMessagesClient>[0]>;
        return: Awaited<ReturnType<GetFixSessionMessagesClient>>;
    };
};
export const getFixSessionMessagesQueryKey = (clientCode: GetFixSessionMessagesPathParams["clientCode"], fixSessionId: GetFixSessionMessagesPathParams["fixSessionId"], params?: GetFixSessionMessages["queryParams"]) => [{ url: "/api/clients/:clientCode/fix-sessions/:fixSessionId/messages", params: { clientCode: clientCode, fixSessionId: fixSessionId } }, ...(params ? [params] : [])] as const;
export type GetFixSessionMessagesQueryKey = ReturnType<typeof getFixSessionMessagesQueryKey>;
export function getFixSessionMessagesQueryOptions(clientCode: GetFixSessionMessagesPathParams["clientCode"], fixSessionId: GetFixSessionMessagesPathParams["fixSessionId"], params?: GetFixSessionMessages["queryParams"], options: GetFixSessionMessages["client"]["parameters"] = {}) {
    const queryKey = getFixSessionMessagesQueryKey(clientCode, fixSessionId, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetFixSessionMessages["data"], GetFixSessionMessages["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/fix-sessions/${fixSessionId}/messages`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets all messages for a given fix session for a given client.
 * @summary Get all messages
 * @link /api/clients/:clientCode/fix-sessions/:fixSessionId/messages
 */
export function useGetFixSessionMessages<TData = GetFixSessionMessages["response"], TQueryData = GetFixSessionMessages["response"], TQueryKey extends QueryKey = GetFixSessionMessagesQueryKey>(clientCode: GetFixSessionMessagesPathParams["clientCode"], fixSessionId: GetFixSessionMessagesPathParams["fixSessionId"], params?: GetFixSessionMessages["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetFixSessionMessages["response"], GetFixSessionMessages["error"], TData, TQueryData, TQueryKey>>;
    client?: GetFixSessionMessages["client"]["parameters"];
} = {}): UseQueryResult<TData, GetFixSessionMessages["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getFixSessionMessagesQueryKey(clientCode, fixSessionId, params);
    const query = useQuery({
        ...getFixSessionMessagesQueryOptions(clientCode, fixSessionId, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetFixSessionMessages["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getFixSessionMessagesSuspenseQueryKey = (clientCode: GetFixSessionMessagesPathParams["clientCode"], fixSessionId: GetFixSessionMessagesPathParams["fixSessionId"], params?: GetFixSessionMessages["queryParams"]) => [{ url: "/api/clients/:clientCode/fix-sessions/:fixSessionId/messages", params: { clientCode: clientCode, fixSessionId: fixSessionId } }, ...(params ? [params] : [])] as const;
export type GetFixSessionMessagesSuspenseQueryKey = ReturnType<typeof getFixSessionMessagesSuspenseQueryKey>;
export function getFixSessionMessagesSuspenseQueryOptions(clientCode: GetFixSessionMessagesPathParams["clientCode"], fixSessionId: GetFixSessionMessagesPathParams["fixSessionId"], params?: GetFixSessionMessages["queryParams"], options: GetFixSessionMessages["client"]["parameters"] = {}) {
    const queryKey = getFixSessionMessagesSuspenseQueryKey(clientCode, fixSessionId, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetFixSessionMessages["data"], GetFixSessionMessages["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/fix-sessions/${fixSessionId}/messages`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets all messages for a given fix session for a given client.
 * @summary Get all messages
 * @link /api/clients/:clientCode/fix-sessions/:fixSessionId/messages
 */
export function useGetFixSessionMessagesSuspense<TData = GetFixSessionMessages["response"], TQueryKey extends QueryKey = GetFixSessionMessagesSuspenseQueryKey>(clientCode: GetFixSessionMessagesPathParams["clientCode"], fixSessionId: GetFixSessionMessagesPathParams["fixSessionId"], params?: GetFixSessionMessages["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetFixSessionMessages["response"], GetFixSessionMessages["error"], TData, TQueryKey>>;
    client?: GetFixSessionMessages["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetFixSessionMessages["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getFixSessionMessagesSuspenseQueryKey(clientCode, fixSessionId, params);
    const query = useSuspenseQuery({
        ...getFixSessionMessagesSuspenseQueryOptions(clientCode, fixSessionId, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetFixSessionMessages["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}